import { ENV, authFetch, parseResponse, SUCCESSARRAY } from "../utils";

function normalizeParent(parent) {
  if (!parent || parent === "null") {
    return null;
  }

  if (typeof parent === "object") {
    return parent._id || parent.id || null;
  }

  return parent;
}

function createCategoryId(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function createSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function createCategoryData(data) {
  return {
    id: createCategoryId(data.id || data.name),
    name: String(data.name || "").trim(),
    slug: createSlug(data.slug || data.name),
    parent: normalizeParent(data.parent),
    description: String(data.description || "").trim(),
    order: Number(data.order),
    visible: data.visible ?? true,
    seo: {
      metaTitle: String(data.seo?.metaTitle || "").trim(),
      metaDescription: String(data.seo?.metaDescription || "").trim(),
    },
  };
}

function createCategoryFormData(data) {
  const categoryData = createCategoryData(data);
  const formData = new FormData();

  formData.append("data", JSON.stringify(categoryData));

  if (data.image instanceof File) {
    formData.append("image", data.image);
  }

  return formData;
}

export class Category {
  async createCategory(data) {
    const url = `${ENV.API_URL}/${ENV.ENDPOINTS.CATEGORY.CATEGORY}`;

    const params = {
      method: "POST",
      body: createCategoryFormData(data),
    };

    const response = await authFetch(url, params);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async getCategories(page = 1, limit = 100, filters = {}) {
    const queryParams = new URLSearchParams({
      page,
      limit,
      ...filters,
    });

    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.CATEGORY.CATEGORIES}` +
      `?${queryParams.toString()}`;

    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async getCategoryTree(filters = {}) {
    const queryParams = new URLSearchParams({
      tree: true,
      ...filters,
    });

    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.CATEGORY.CATEGORIES}` +
      `?${queryParams.toString()}`;

    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async getCategory(categoryId) {
    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.CATEGORY.CATEGORY}` +
      `/${categoryId}`;

    const response = await authFetch(url);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async updateCategory(categoryId, data) {
    const categoryData = createCategoryData(data);

    if (data.removeImage === true) {
      categoryData.removeImage = true;
    }

    const formData = new FormData();

    formData.append("data", JSON.stringify(categoryData));

    if (data.image instanceof File) {
      formData.append("image", data.image);
    }

    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.CATEGORY.CATEGORY}` +
      `/${categoryId}`;

    const params = {
      method: "PATCH",
      body: formData,
    };

    const response = await authFetch(url, params);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }

  async deleteCategory(categoryId) {
    const url =
      `${ENV.API_URL}/${ENV.ENDPOINTS.CATEGORY.CATEGORY}` +
      `/${categoryId}`;

    const params = {
      method: "DELETE",
    };

    const response = await authFetch(url, params);
    const result = await parseResponse(response);

    if (!SUCCESSARRAY.includes(response.status)) {
      throw result;
    }

    return result;
  }
}